import { useEffect, useRef } from "react";
import * as THREE from "three";

const ORBITS = [
    { color: 0x4caf50, tilt: 0.5, speed: 0.32, radius: 1.9, node: 0.13 },
    { color: 0xf16622, tilt: -0.7, speed: -0.26, radius: 2.25, node: 0.15 },
    { color: 0x8fa0ff, tilt: 1.15, speed: 0.21, radius: 2.6, node: 0.12 },
    { color: 0xffffff, tilt: -1.35, speed: -0.17, radius: 2.95, node: 0.11 },
];

export const OrbitModel = ({ className = "" }) => {
    const mountRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.1, 100);
        camera.position.set(0, 0.7, 7.6);
        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        mount.appendChild(renderer.domElement);

        const root = new THREE.Group();
        scene.add(root);

        const coreGeo = new THREE.IcosahedronGeometry(0.55, 0);
        const core = new THREE.Mesh(coreGeo, new THREE.MeshBasicMaterial({ color: 0xf16622 }));
        root.add(core);
        const coreWire = new THREE.Mesh(
            coreGeo,
            new THREE.MeshBasicMaterial({ color: 0xffffff, wireframe: true, transparent: true, opacity: 0.22 })
        );
        coreWire.scale.setScalar(1.3);
        root.add(coreWire);

        const nodes = [];
        ORBITS.forEach((o) => {
            const g = new THREE.Group();
            g.rotation.x = o.tilt;
            const ring = new THREE.Mesh(
                new THREE.TorusGeometry(o.radius, 0.0045, 8, 220),
                new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.16 })
            );
            ring.rotation.x = Math.PI / 2;
            g.add(ring);
            const node = new THREE.Mesh(
                new THREE.SphereGeometry(o.node, 24, 24),
                new THREE.MeshBasicMaterial({ color: o.color })
            );
            const halo = new THREE.Mesh(
                new THREE.SphereGeometry(o.node * 2, 24, 24),
                new THREE.MeshBasicMaterial({ color: o.color, transparent: true, opacity: 0.16 })
            );
            node.add(halo);
            g.add(node);
            root.add(g);
            nodes.push({ node, ...o });
        });

        const PN = 280;
        const pos = new Float32Array(PN * 3);
        for (let i = 0; i < PN; i++) {
            pos[i * 3] = (Math.random() - 0.5) * 9;
            pos[i * 3 + 1] = (Math.random() - 0.5) * 6;
            pos[i * 3 + 2] = (Math.random() - 0.5) * 4 - 1;
        }
        const pGeo = new THREE.BufferGeometry();
        pGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
        const pMat = new THREE.PointsMaterial({ color: 0x8fa0ff, size: 0.02, transparent: true, opacity: 0.45 });
        const stars = new THREE.Points(pGeo, pMat);
        scene.add(stars);

        let mx = 0;
        let my = 0;
        const onMouse = (e) => {
            const rect = mount.getBoundingClientRect();
            mx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
            my = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
        };
        mount.addEventListener("mousemove", onMouse);

        let raf;
        const clock = new THREE.Clock();
        const animate = () => {
            const t = clock.getElapsedTime();
            nodes.forEach((n) => {
                const a = t * n.speed;
                n.node.position.set(Math.cos(a) * n.radius, 0, Math.sin(a) * n.radius);
            });
            core.rotation.y = t * 0.4;
            coreWire.rotation.y = -t * 0.25;
            root.rotation.y = t * 0.08 + mx * 0.28;
            root.rotation.x = my * 0.14;
            stars.rotation.y = t * 0.01;
            renderer.render(scene, camera);
            raf = requestAnimationFrame(animate);
        };
        animate();

        const onResize = () => {
            camera.aspect = mount.clientWidth / mount.clientHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(mount.clientWidth, mount.clientHeight);
        };
        window.addEventListener("resize", onResize);

        return () => {
            cancelAnimationFrame(raf);
            mount.removeEventListener("mousemove", onMouse);
            window.removeEventListener("resize", onResize);
            if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
            coreGeo.dispose();
            pGeo.dispose();
            pMat.dispose();
            renderer.dispose();
        };
    }, []);

    return <div ref={mountRef} className={className} data-testid="orbit-model" />;
};
